import { useEffect, useState } from "react";
import { Bell, Send, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";

export default function NoticesAdmin() {
  const [notices, setNotices] = useState([]);
  const [yearGroups, setYearGroups] = useState([]);
  const [form, setForm] = useState({ title: "", body: "", audience: "all" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/school/notices").then((r) => setNotices(r.data)).catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    load();
    api.get("/school/profile").then((r) => setYearGroups(r.data.year_groups)).catch(() => {});
  }, []);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/school/notices", form);
      toast.success(data.emails_sent ? `Notice published — ${data.emails_sent} email(s) sent` : "Notice published (in-app)");
      setForm({ title: "", body: "", audience: "all" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/school/notices/${id}`);
      toast.success("Notice deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="notices-admin-page">
      <span className="nb-label">Notices</span>
      <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2">Push a notice</h1>
      <p className="text-zinc-500 mt-2 max-w-xl">Notices appear instantly in pupil apps, and are emailed to parent addresses on file (when SMTP is configured).</p>

      <div className="mt-8 grid lg:grid-cols-5 gap-6 items-start">
        <div className="nb-card p-6 lg:col-span-2 lg:sticky lg:top-6">
          <h2 className="font-display font-bold text-lg flex items-center gap-2"><Bell className="w-5 h-5" /> New notice</h2>
          <form onSubmit={send} className="mt-5 space-y-4">
            <div>
              <label className="nb-label" htmlFor="notice-audience">Audience</label>
              <select id="notice-audience" className="nb-input mt-2" data-testid="notice-audience-select"
                value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}>
                <option value="all">Whole school</option>
                {yearGroups.map((y) => <option key={y} value={y}>{y} only</option>)}
              </select>
            </div>
            <div>
              <label className="nb-label" htmlFor="notice-title">Title</label>
              <input id="notice-title" className="nb-input mt-2" data-testid="notice-title-input" placeholder="e.g. Sports day Friday"
                value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required maxLength={140} />
            </div>
            <div>
              <label className="nb-label" htmlFor="notice-body">Message</label>
              <textarea id="notice-body" className="nb-input mt-2 min-h-[120px] resize-y" data-testid="notice-body-input" placeholder="Write the notice…"
                value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} required maxLength={4000} />
            </div>
            <button type="submit" disabled={busy} data-testid="notice-send-btn" className="nb-btn w-full bg-[#FDE68A] py-2.5">
              <Send className="w-4 h-4" /> {busy ? "Publishing…" : "Publish notice"}
            </button>
          </form>
        </div>

        <div className="lg:col-span-3 space-y-4">
          <h2 className="font-display font-bold text-lg">Published notices</h2>
          {notices.map((n) => (
            <div key={n.id} className="nb-card p-5" data-testid={`notice-card-${n.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-display font-bold text-lg">{n.title}</h3>
                    <span className={`px-2 py-0.5 rounded-lg border-2 border-zinc-900 text-xs font-bold ${n.audience === "all" ? "bg-[#A7E4D3]" : "bg-[#D8B4FE]"}`}>
                      {n.audience === "all" ? "Whole school" : n.audience}
                    </span>
                  </div>
                  <p className="text-sm text-zinc-600 mt-2 whitespace-pre-wrap">{n.body}</p>
                  <p className="text-xs text-zinc-400 mt-3 font-semibold">
                    {new Date(n.created_at).toLocaleString("en-GB")} · Read by {n.read_count || 0} pupil(s)
                    {n.emails_sent ? ` · ${n.emails_sent} emailed` : ""}
                  </p>
                </div>
                <button onClick={() => remove(n.id)} data-testid={`notice-delete-${n.id}`}
                  className="p-2 shrink-0 rounded-lg border-2 border-zinc-900 bg-red-100 hover:bg-red-200 transition-colors duration-150">
                  <Trash2 className="w-4 h-4 text-red-700" />
                </button>
              </div>
            </div>
          ))}
          {notices.length === 0 && (
            <div className="nb-card p-10 text-center" data-testid="notices-empty">
              <Users className="w-8 h-8 mx-auto text-zinc-300 mb-3" />
              <p className="text-zinc-500 text-sm">No notices yet. Publish your first one.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
